/**
 * Analiz sekmesi - teknik analiz raporu (kullanici 13.10: "tam bir teknik analizci olsun").
 *
 * Kaynak: `/api/symbols/{symbol}/technical` (backend strategy/technical.py) - 15dk / 1s /
 * 4s / 1g gostergeler, birlestirilmis destek/direnc, formasyonlar ve kural tabanli
 * analist notu. Arayuz kendi gostergesini HESAPLAMAZ; yalnizca gosterir.
 *
 * Rapor grafigi ANLATIR, AL/SAT karari VERMEZ: eski "AL / Izle / Cik" etiketi testlerde
 * dogrulanmadigi icin kaldirildi; yerine kosullu senaryolar ("su kirilirsa -> su seviye").
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import type { Candles, KeyLevel, TechnicalReport, TechnicalTimeframe } from "../api/types";
import { pct, price } from "../lib/format";
import { TradingChart } from "./TradingChart";
import { Card, Change, Empty, Pill, Row, SearchField, type Tone } from "./Ui";

const ANALYSIS_EXAMPLE_SYMBOLS = ["BTC", "ETH", "SOL", "BNB", "XRP"];

const TF_LABEL: Record<string, string> = { "15m": "15dk", "1h": "1s", "4h": "4s", "1d": "1g" };
const TF_LONG: Record<string, string> = {
  "15m": "15 dakikalık",
  "1h": "1 saatlik",
  "4h": "4 saatlik",
  "1d": "Günlük",
};

/** Sembolu Binance spot formatina normallestirir - "btc" da "BTCUSDT" da calisir. */
function normalizeSymbolInput(raw: string): string {
  const cleaned = raw.trim().toUpperCase().replace(/\s+/g, "");
  if (!cleaned) return "";
  return cleaned.endsWith("USDT") ? cleaned : `${cleaned}USDT`;
}

function outlookTone(outlook: string): Tone {
  if (outlook === "up") return "up";
  if (outlook === "down") return "down";
  return "neutral";
}

function trendCell(trend: string): { text: string; tone: Tone } {
  if (trend === "up") return { text: "↑ Yükseliş", tone: "up" };
  if (trend === "down") return { text: "↓ Düşüş", tone: "down" };
  return { text: "→ Yatay", tone: "neutral" };
}

function rsiTone(rsi: number | null): Tone {
  if (rsi == null) return "neutral";
  if (rsi >= 70) return "warn";
  if (rsi >= 55) return "up";
  if (rsi <= 30) return "warn";
  if (rsi <= 45) return "down";
  return "neutral";
}

function macdCell(tf: TechnicalTimeframe): { text: string; tone: Tone } {
  switch (tf.macd_state) {
    case "bull_cross":
      return { text: "↑ Kesişim", tone: "up" };
    case "bear_cross":
      return { text: "↓ Kesişim", tone: "down" };
    case "bull":
      return { text: tf.macd_rising ? "+ Güçlü" : "+ Zayıf", tone: "up" };
    case "bear":
      return { text: tf.macd_rising ? "− Toparlıyor" : "− Güçlü", tone: "down" };
    default:
      return { text: "—", tone: "neutral" };
  }
}

function stackCell(stack: string): { text: string; tone: Tone } {
  if (stack === "up") return { text: "Yukarı dizili", tone: "up" };
  if (stack === "down") return { text: "Aşağı dizili", tone: "down" };
  return { text: "Karışık", tone: "neutral" };
}

function Cell({ text, tone }: { text: string; tone: Tone }) {
  return <span className={`ta-cell tone-${tone}`}>{text}</span>;
}

function TimeframeTable({ frames }: { frames: TechnicalTimeframe[] }) {
  return (
    <div className="ta-table-wrap">
      <table className="ta-table">
        <thead>
          <tr>
            <th>Periyot</th>
            <th>Trend</th>
            <th>UT Bot</th>
            <th>RSI</th>
            <th>MACD</th>
            <th>EMA 20/50/200</th>
            <th>Hacim</th>
          </tr>
        </thead>
        <tbody>
          {frames.map((tf) => {
            const trend = trendCell(tf.trend);
            const macd = macdCell(tf);
            const stack = stackCell(tf.ema_stack);
            return (
              <tr key={tf.timeframe}>
                <th scope="row">{TF_LABEL[tf.timeframe] ?? tf.timeframe}</th>
                <td>
                  <Cell {...trend} />
                </td>
                <td>
                  <Cell
                    text={tf.ut_position === 1 ? "AL" : "SAT"}
                    tone={tf.ut_position === 1 ? "up" : "down"}
                  />
                  {tf.ut_bars_since != null ? (
                    <span className="ta-sub">{tf.ut_bars_since} mum</span>
                  ) : null}
                </td>
                <td>
                  <Cell text={tf.rsi != null ? tf.rsi.toFixed(0) : "—"} tone={rsiTone(tf.rsi)} />
                </td>
                <td>
                  <Cell {...macd} />
                </td>
                <td>
                  <Cell {...stack} />
                </td>
                <td>
                  <Cell
                    text={tf.rel_volume != null ? `${tf.rel_volume.toFixed(1).replace(".", ",")}x` : "—"}
                    tone={
                      tf.rel_volume == null
                        ? "neutral"
                        : tf.rel_volume >= 1.5
                          ? "up"
                          : tf.rel_volume <= 0.6
                            ? "warn"
                            : "neutral"
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LevelList({ title, levels, side }: { title: string; levels: KeyLevel[]; side: "up" | "down" }) {
  return (
    <div className="ta-levels-col">
      <div className={`ta-levels-title tone-${side}`}>{title}</div>
      {levels.length === 0 ? (
        <p className="note">Yakında belirgin seviye yok.</p>
      ) : (
        <ol className="ta-levels">
          {levels.map((k) => (
            <li key={k.price}>
              <div className="ta-level-row">
                <span className="ta-level-price">{price(k.price)}</span>
                <span className={`ta-level-dist tone-${side}`}>{pct(k.distance_pct, 1)}</span>
              </div>
              <div className="ta-level-src">{k.sources_tr.join(" · ")}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function IndicatorDetail({ tf }: { tf: TechnicalTimeframe }) {
  const pctOf = (value: number | null) =>
    value == null ? "" : ` (${pct((value / tf.close - 1) * 100, 1)})`;
  return (
    <div className="rows">
      <Row label="Kapanış">{price(tf.close)}</Row>
      <Row label="EMA 20">
        {tf.ema20 != null ? price(tf.ema20) + pctOf(tf.ema20) : "—"}
      </Row>
      <Row label="EMA 50">
        {tf.ema50 != null ? price(tf.ema50) + pctOf(tf.ema50) : "—"}
      </Row>
      <Row label="EMA 200">
        {tf.ema200 != null ? price(tf.ema200) + pctOf(tf.ema200) : "yetersiz veri"}
      </Row>
      <Row label="UT Bot takip çizgisi">
        {tf.ut_trailing_stop != null ? price(tf.ut_trailing_stop) + pctOf(tf.ut_trailing_stop) : "—"}
      </Row>
      <Row label="RSI (14)">{tf.rsi != null ? tf.rsi.toFixed(1).replace(".", ",") : "—"}</Row>
      <Row label="MACD / sinyal">
        {tf.macd != null && tf.macd_signal != null
          ? `${price(tf.macd)} / ${price(tf.macd_signal)}`
          : "—"}
      </Row>
      <Row label="Bollinger (20,2)">
        {tf.bb_lower != null && tf.bb_upper != null
          ? `${price(tf.bb_lower)} – ${price(tf.bb_upper)}${tf.squeeze ? " · sıkışma" : ""}`
          : "—"}
      </Row>
      <Row label="Bant içindeki yer (%B)">
        {tf.bb_percent != null ? `%${Math.round(tf.bb_percent * 100)}` : "—"}
      </Row>
      <Row label="Ortalama mum boyu (ATR)">
        {tf.atr_pct != null ? `%${tf.atr_pct.toFixed(2).replace(".", ",")}` : "—"}
      </Row>
      <Row label="Son tepe / son dip">
        {`${tf.swing_high != null ? price(tf.swing_high) : "—"} / ${
          tf.swing_low != null ? price(tf.swing_low) : "—"
        }`}
      </Row>
    </div>
  );
}

export function AnalysisScreen({
  theme,
  onSelect,
  onError,
}: {
  theme: "light" | "dark";
  onSelect: (symbol: string) => void;
  onError: (message: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [symbol, setSymbol] = useState<string | null>(null);
  const [report, setReport] = useState<TechnicalReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTf, setActiveTf] = useState("4h");
  const [candles, setCandles] = useState<Candles | null>(null);
  const [chartLoading, setChartLoading] = useState(false);

  const runAnalysis = (raw: string) => {
    const next = normalizeSymbolInput(raw);
    if (next) setSymbol(next);
  };

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    setReport(null);
    void (async () => {
      try {
        const payload = await api.technical(symbol);
        if (!cancelled) setReport(payload);
      } catch (error) {
        if (!cancelled) {
          onError(error instanceof ApiError ? error.message : `${symbol} analiz edilemedi`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol, onError]);

  const loadChart = useCallback(async () => {
    if (!symbol) return;
    setChartLoading(true);
    try {
      setCandles(await api.candles(symbol, activeTf, 300));
    } catch (error) {
      setCandles(null);
      onError(error instanceof ApiError ? error.message : "Grafik alınamadı");
    } finally {
      setChartLoading(false);
    }
  }, [symbol, activeTf, onError]);

  useEffect(() => {
    if (report) void loadChart();
  }, [report, loadChart]);

  const activeFrame = report?.timeframes.find((tf) => tf.timeframe === activeTf) ?? null;
  const patterns =
    report?.timeframes.flatMap((tf) => tf.patterns.map((p) => ({ tf: tf.timeframe, p }))) ?? [];

  return (
    <>
      <h2 className="section-title">Teknik Analiz</h2>
      <p className="note">
        Bir coin yaz (örn. <strong>BTC</strong>). 15 dakikalıktan günlüğe trend, momentum,
        destek-direnç, formasyonlar ve kısa bir analist notu görürsün.
      </p>

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          runAnalysis(query);
        }}
      >
        <SearchField value={query} onChange={setQuery} placeholder="Sembol yaz (örn. BTC, ETH, SOL...)" />
        <button type="submit" className="btn btn-primary" disabled={!query.trim()}>
          Analiz Et
        </button>
      </form>

      <div className="chips">
        {ANALYSIS_EXAMPLE_SYMBOLS.map((example) => (
          <button
            key={example}
            type="button"
            className="chip"
            onClick={() => {
              setQuery(example);
              runAnalysis(example);
            }}
          >
            {example}
          </button>
        ))}
      </div>

      {loading ? <Empty title="Analiz ediliyor…" detail="4 zaman diliminin mumları çekiliyor." /> : null}

      {!loading && report ? (
        <>
          <Card>
            <div className="ta-head">
              <div>
                <div className="analysis-symbol">{report.base_asset}/USDT</div>
                <div className="ta-head-price">
                  {price(report.price)} <Change value={report.change_24h_pct} />
                  <span className="ta-sub"> 24s</span>
                </div>
              </div>
              <div className="ta-head-right">
                <Pill tone={outlookTone(report.outlook)}>{report.outlook_tr}</Pill>
                <span className="ta-sub">
                  {report.up_count}/{report.timeframes.length} periyot yükselişte
                </span>
              </div>
            </div>
          </Card>

          <Card title="Analist notu">
            <div className="ta-note">
              {report.note_tr.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
            {report.scenarios_tr.length > 0 ? (
              <ul className="ta-scenarios">
                {report.scenarios_tr.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}
            <p className="note ta-disclaimer">{report.disclaimer_tr}</p>
          </Card>

          <Card title="Zaman dilimleri">
            <TimeframeTable frames={report.timeframes} />
          </Card>

          <Card title="Kritik seviyeler">
            <div className="ta-levels-grid">
              <LevelList title="Dirençler (yukarıda)" levels={report.resistances} side="down" />
              <LevelList title="Destekler (aşağıda)" levels={report.supports} side="up" />
            </div>
            {report.fib_levels.length > 0 ? (
              <p className="note" style={{ marginTop: 10 }}>
                4 saatlik Fibonacci ({report.fib_direction === "up" ? "son yükselişin geri çekilmesi" : "son düşüşün toparlanması"}):{" "}
                {report.fib_levels.map((f) => `${f.name} ${price(f.price)}`).join(" · ")}
              </p>
            ) : null}
          </Card>

          {patterns.length > 0 ? (
            <Card title="Formasyonlar ve uyarılar">
              <ul className="ta-patterns">
                {patterns.map(({ tf, p }) => (
                  <li key={`${tf}:${p.kind}`}>
                    <div className="ta-pattern-head">
                      <span className="chip chip-static">{TF_LABEL[tf] ?? tf}</span>
                      <strong>{p.title_tr}</strong>
                    </div>
                    <p className="note">{p.detail_tr}</p>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <div className="chips" role="group" aria-label="Zaman dilimi">
            {report.timeframes.map((tf) => (
              <button
                key={tf.timeframe}
                type="button"
                className="chip"
                aria-pressed={tf.timeframe === activeTf}
                onClick={() => setActiveTf(tf.timeframe)}
              >
                {TF_LONG[tf.timeframe] ?? tf.timeframe}
              </button>
            ))}
          </div>

          <Card title={`Grafik — ${TF_LONG[activeTf] ?? activeTf}`}>
            <div className="chart-shell">
              {chartLoading ? (
                <div className="skeleton" style={{ height: 260 }} />
              ) : (
                <TradingChart data={candles} theme={theme} height={260} />
              )}
            </div>
          </Card>

          {activeFrame ? (
            <Card title={`Gösterge detayı — ${TF_LONG[activeTf] ?? activeTf}`}>
              <IndicatorDetail tf={activeFrame} />
            </Card>
          ) : null}

          <div className="btn-row" style={{ marginTop: 10 }}>
            <button type="button" className="btn btn-primary" onClick={() => onSelect(report.symbol)}>
              Sinyal Kartını Aç
            </button>
          </div>
        </>
      ) : null}

      {!loading && symbol && !report ? (
        <Empty title="Analiz alınamadı" detail="Sembolü kontrol edip tekrar deneyin." />
      ) : null}
    </>
  );
}
